/**
 * Circuito é o único catálogo que **não cabe em memória**: são milhares por
 * projeto (630 só no projeto 6) e a página é de 200. Por isso ele é busca no
 * servidor, com o freio da regra 2: dois caracteres no mínimo, 400 ms de espera,
 * e nenhuma tecla virando requisição sozinha.
 *
 * A resposta traz `corHabitual` e `tipoHabitual` — a cor e o tipo que mais
 * aparecem nos pinouts daquele circuito. É o *preenche* do campo de busca:
 * escolheu o circuito, a cor entra sozinha. Vem do mesmo agrupamento que já
 * calcula o `qtd`, então não custa consulta a mais.
 */

import { useCallback, useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { obter } from "./cliente"
import { BUSCA_MINIMO_CARACTERES, chaves } from "./query"
import type { CircuitoResponse, PaginaResponse } from "./tipos"
import type { ItemDeCatalogo } from "./catalogos"

export type Habituais = { corId: number; cor: string; tipo: number }

export function usarCircuitos(projetoId: number) {
  const [termo, setTermo] = useState("")

  const { data, isFetching } = useQuery({
    queryKey: chaves.circuitos(projetoId, termo),
    enabled: projetoId > 0 && termo.length >= BUSCA_MINIMO_CARACTERES,
    // O termo já vem com espera do campo; aqui basta não revalidar sozinho.
    staleTime: 5 * 60_000,
    queryFn: () =>
      obter<PaginaResponse<CircuitoResponse>>("api/circuitos", {
        projetoId, descricoes: termo, ordem: "descricao", pagina: 1,
      }),
  })

  const brutos = useMemo(() => data?.itens ?? [], [data])

  /**
   * Um item por **circuito**, com o nome inteiro (`1BRPT1.A`) como código e o
   * apelido ao lado.
   *
   * Agrupava pelo circuito-base (`1BRPT1`), para não listar os trechos `.A`,
   * `.B`, `.C` como opções diferentes. Só que cada trecho é um circuito no banco,
   * e o pinout grava um deles: o item agrupado mostrava `1BRPT1` — o ".A" sumia
   * do campo ao confirmar — e levava o id de um trecho qualquer do grupo. Trecho
   * que não era o do grupo virava "novo" mesmo existindo. O legado também
   * listava o nome inteiro.
   */
  const itens = useMemo(
    () => brutos
      .map((c): ItemDeCatalogo => ({ id: c.id, codigo: c.descricao, rotulo: c.apelido ?? "", usos: c.qtd }))
      .sort((a, b) => b.usos - a.usos || a.codigo!.localeCompare(b.codigo!, "pt-BR")),
    [brutos])

  const habituais = useCallback(
    (id: number): Habituais | null => {
      const c = brutos.find((x) => x.id === id)
      if (!c || !c.corHabitualId) return null
      return { corId: c.corHabitualId, cor: c.corHabitual, tipo: c.tipoHabitual }
    },
    [brutos],
  )

  return { itens, carregando: isFetching, buscar: setTermo, habituais }
}
